<template>
  <v-form
    ref="form"
    v-model="formValid"
    dense
  >
    <confirmation-dialog ref="confirm" />
    <v-card>
      <v-card-title>Edit Template</v-card-title>

      <v-card-text class="py-0">
        <v-text-field
          ref="templateName"
          v-model="template.name"
          :rules="[validateRequired]"
          label="Template Name"
          required
          hint="Note: This will also be the menu item name."
          persistent-hint
        />

        <v-text-field
          ref="creditCost"
          v-model="template.creditCost"
          type="number"
          min="0"
          :rules="[validateNonNegative]"
          label="Credit Cost"
          persistent-hint
        />

        <v-text-field
          ref="templateShortName"
          v-model="template.shortName"
          :rules="[validateRequired]"
          label="Template Short Name"
          required
          maxlength="10"
          counter="10"
          hint="Note: This will appear where a shorter name is required."
          persistent-hint
        />

        <v-text-field
          ref="templateDescription"
          v-model="template.description"
          :rules="[validateRequired]"
          label="Template Description"
          required
          maxlength="100"
          counter="100"
        />

        <document-type-select v-model="template.documentType" />

        <v-select
          v-if="allowCategorySelection"
          ref="templateCategories"
          v-model="template.categories"
          :rules="[validateRequired]"
          :items="appTemplateCategories"
          multiple
          label="Template Categories"
          required
          item-text="name"
          item-value=".key"
        />

        <v-text-field
          ref="templateGuideUrl"
          v-model="template.guideUrl"
          :rules="[
            validateRequired,
            (v) => !guideUrlDirty || isUrl(v) || 'Invalid URL.'
          ]"
          label="Guide Url*"
          placeholder="e.g. https://authentigate.io/last-will-and-testament-guide/"
          type="url"
          required
          @blur="guideUrlDirty = true"
        />

        <v-text-field
          ref="templateAuthorUrl"
          v-model="template.authorUrl"
          :rules="[
            (v) => !authorUrlDirty || !v || isUrl(v) || 'Invalid URL.'
          ]"
          label="Author Url"
          placeholder="e.g. https://authentigate.io/authors/marcus-mccarthy"
          hint="If specified, then an Author Contact Details link will be shown in the help panel for this template"
          type="url"
          required
          @blur="authorUrlDirty = true"
        />

        <v-autocomplete
          ref="formstackForm"
          v-model="template.formstack.id"
          :loading="formstackLoading"
          :items="formstackFiltered"
          label="Formstack Form"
          item-text="name"
          item-value="id"
          hide-details
          @input="
            () => {
              $refs.templateActive.validate()
              formstackFormDefIssues = []
            }
          "
        />
        <v-btn
          small
          text
          color="primary"
          @click="
            fetchFormstackFieldsData(template.formstack.id)
            formstackFieldsDialog = true
          "
        >
          Show field detail
        </v-btn>

        <v-autocomplete
          ref="webmergeDocument"
          v-model="template.webmerge.id"
          :loading="webmergeLoading"
          :items="webmergeFiltered"
          label="Webmerge Document"
          item-text="name"
          item-value="id"
          @input="() => $refs.templateActive.validate()"
        />

        <v-select
          ref="expertLawyers"
          v-model="template.expertLawyers"
          :items="lawyers"
          label="Expert Lawyers"
          multiple
          item-text="name"
          item-value="id"
        />

        <v-switch
          ref="templateActive"
          v-model="template.active"
          label="Active"
        />
        <v-switch
          v-model="template.preview"
          label="Preview"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :loading="saving"
          :disabled="saving || template['.key'] === ''"
          text
          class="margin-left-5 margin-right-0 min-width-50"
          color="error"
          small
          @click.native="deleteTemplate"
        >
          Delete
        </v-btn>
        <v-btn
          :loading="saving"
          :disabled="saving"
          text
          class="margin-left-5 margin-right-0 min-width-50"
          small
          @click.native="cancel"
        >
          Cancel
        </v-btn>
        <v-btn
          :loading="saving"
          :disabled="saving || !formValid"
          color="primary"
          class="margin-left-5 margin-right-0 min-width-50"
          small
          @click.native="saveTemplateToFirestore"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog
      v-model="formstackFieldsDialog"
      persistent
    >
      <v-card>
        <v-card-title>
          Formstack Fields
          <v-spacer />
          <v-btn
            color="primary"
            small
            @click="formstackFieldsDialog = false"
          >
            Close
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="v-table__overflow">
            <table class="v-datatable v-table theme--light">
              <thead v-show="formstackFieldsLoading">
                <tr class="v-datatable__progress">
                  <th
                    colspan="0"
                    class="column"
                  >
                    <v-progress-linear indeterminate />
                  </th>
                </tr>
              </thead>
              <thead v-show="!formstackFieldsLoading">
                <th>Section</th>
                <th>Section Heading / Section Text / Field Name</th>
                <th>Type</th>
              </thead>
              <tbody v-show="!formstackFieldsLoading">
                <template v-for="field in formstackFields">
                  <tr :key="field.id">
                    <td
                      :class="
                        field.type === 'section' ? 'font-weight-bold' : ''
                      "
                    >
                      {{ field.type === 'section' ? field.type : '' }}
                    </td>
                    <td
                      :class="
                        field.type === 'section' ? 'font-weight-bold' : ''
                      "
                    >
                      {{
                        field.name ||
                          field.section_heading ||
                          field.section_text ||
                          '--subsection--'
                      }}
                    </td>
                    <td
                      :class="
                        field.type === 'section' ? 'font-weight-bold' : ''
                      "
                    >
                      {{ field.type }}
                    </td>
                  </tr>
                  <tr
                    v-if="
                      field.type === 'section' &&
                        field.section_heading &&
                        field.section_text
                    "
                    :key="field.id + '_section_text'"
                  >
                    <td />
                    <td>{{ field.section_text }}</td>
                    <td />
                  </tr>
                  <tr
                    v-if="field.options"
                    :key="field.id + '_options'"
                  >
                    <td />
                    <td>{{ field.options }}</td>
                    <td />
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </v-card-text>

        <v-card-actions class="justify-center" />
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="formstackIssuesDialog"
      persistent
    >
      <v-card>
        <v-card-title>
          Formstack Form Definition Issues
          <v-spacer />
          <v-btn
            color="primary"
            small
            @click="formstackIssuesDialog = false"
          >
            Close
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="v-table__overflow">
            <table class="v-datatable v-table theme--light">
              <thead>
                <th class="text-left">
                  Field
                </th>
                <th class="text-left">
                  Issue
                </th>
              </thead>
              <tbody>
                <template v-for="fieldIssue in formstackFormDefIssues">
                  <tr :key="fieldIssue.join('/')">
                    <td>{{ fieldIssue[0] }}</td>
                    <td>{{ fieldIssue[1] }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </v-card-text>

        <v-card-actions class="justify-center" />
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import { firebase, auth } from '../firebase'
import { saveTemplate, deleteTemplate } from '@/dataAccess/templates'
import { validateRequired, validateNonNegative } from '@/utils/validationRules'
import { isNewRecord } from '@/dataAccess/queries'
import { ASSET_SALE } from '@/dataAccess/documentTypes'
import DocumentTypeSelect from './DocumentTypeSelect.vue'
import ConfirmationDialog from '@/components/ConfirmationDialog.vue'

const jsonpackImport = () => import('jsonpack')
const spacetimeImport = () => import('spacetime')

export default {
  name: 'EditTemplate',
  components: {
    ConfirmationDialog,
    DocumentTypeSelect
  },
  props: {
    selectedTemplate: { type: Object, default: () => ({}) },
    appTemplateCategories: { type: Array, default: () => [] },
    allowCategorySelection: { type: Boolean, default: false },
    lawyers: { type: Array, default: () => [] },
    isOpen: { type: Boolean, default: false }
  },
  data: function () {
    return {
      formValid: false,
      formstackLoading: true,
      webmergeLoading: true,
      formstackForms: [],
      formstackFields: [],
      formstackFieldsLoading: false,
      webmergeDocuments: [],
      formstackFieldsDialog: false,
      formstackIssuesDialog: false,
      formstackFormDefIssues: [],
      template: this.selectedTemplate,
      authorUrlDirty: false,
      guideUrlDirty: false,
      saving: false,
      templateCategoriesLoading: true
    }
  },
  computed: {
    formstackFiltered() {
      return this.formstackForms
    },
    webmergeFiltered() {
      return this.webmergeDocuments
    },
    isNewTemplate() {
      return isNewRecord(this.selectedTemplate)
    }
  },
  watch: {
    isOpen: {
      handler: function (newVal, oldVal) {
        if (newVal) {
          this.loadTemplate()
        }
      },
      immediate: true
    }
  },
  created() {
    this.fetchWebmergeData()
    this.fetchFormstackData()
  },
  methods: {
    validateRequired,
    validateNonNegative,
    async fetchFormstackData() {
      try {
        const response = await this.$fireGet(
          'document/formstack/forms',
          auth,
          {}
        )
        if (response.status !== 200) {
          this.$showErrorbar('Error fetching Formstack forms.')
          return
        }
        this.formstackForms = response.data
        this.formstackLoading = false
      } catch (e) {
        this.$showErrorbar('Error fetching Formstack forms.')
        this.$Sentry.captureException(e)
      }
    },
    async fetchWebmergeData() {
      try {
        const response = await this.$fireGet(
          'document/formstack/documents',
          auth,
          {}
        )
        if (response.status !== 200) {
          this.$showErrorbar('Error fetching Webmerge documents')
          return
        }
        this.webmergeDocuments = response.data
        this.webmergeLoading = false
      } catch (e) {
        this.$showErrorbar('Error fetching Webmerge documents')
        this.$Sentry.captureException(e)
      }
    },
    async fetchFormstackFieldsData(id) {
      this.formstackFieldsLoading = true
      try {
        const response = await this.$fireGet(
          `document/formstack/forms/${id}/fields`,
          auth,
          {}
        )
        if (response.status !== 200) {
          this.$showErrorbar('Error fetching Formstack form fields')
          return
        }
        this.formstackFields = response.data
        this.formstackFieldsLoading = false
        return response.data
      } catch (e) {
        this.$showErrorbar('Error fetching Formstack form fields')
        this.$Sentry.captureException(e)
      }
      this.formstackFieldsLoading = false
    },
    validateFormstackFormDef(formDef) {
      var issues = []

      const genNameIdEtc = (field) =>
        `${field.name || field.type + '_' + field.sort} (${field.id}${
          field.name ? '' : ' ' + (field.section_heading || field.section_text)
        })`

      // missing option fields
      formDef.fields
        .filter(
          (field) =>
            ['radio', 'checkbox', 'select'].includes(field.type) &&
            (!('options' in field) || !field.options.length)
        )
        .forEach((field) => {
          issues.push([
            genNameIdEtc(field),
            `Is '${field.type}' field but has no options specified`
          ])
        })

      // bad logic fields
      const fieldsById = formDef.fields.reduce(
        (acc, field) => ({ ...acc, [field.id + '']: field }),
        {}
      )

      formDef.fields
        .filter((field) => field.logic && field.logic.length)
        .forEach((field) => {
          const fieldNameIdEtc = genNameIdEtc(field)

          field.logic.checks.forEach((check) => {
            const checkFieldId = check.field + ''
            const checkField = fieldsById[checkFieldId]

            if (!checkField) {
              issues.push([
                fieldNameIdEtc,
                `The check field '${checkFieldId}' does not exist. ${checkFieldId} ${check.condition} ${check.option}`
              ])
            } else {
              if (['radio', 'checkbox', 'select'].includes(checkField.type)) {
                if (!('options' in checkField) || !checkField.options.length) {
                  // check field has no options
                  issues.push([
                    fieldNameIdEtc,
                    `The check field '${genNameIdEtc(checkField)}' is '${
                      field.type
                    }' field but has no options specified`
                  ])
                } else {
                  const optionDoesntExist = !checkField.options.some(
                    (option) => option.label + '' === check.option + ''
                  )
                  if (optionDoesntExist) {
                    issues.push([
                      fieldNameIdEtc,
                      `The check field '${genNameIdEtc(
                        checkField
                      )}' doesn't have specified label value ('${
                        check.option
                      }')`
                    ])
                  }
                }
              }
            }
          })
        })

      return issues
    },
    async saveTemplateToFirestore() {
      this.saving = true

      try {
        var template = { ...this.template, creditCost: parseInt(this.template.creditCost) }

        if (template.formstack.id) {
          const formstackForm = this.formstackForms.find(
            (item) => +item.id === +template.formstack.id
          )

          // only copy fields array as /forms/:id endpoint uses UTC timestrings instead of locale timestrings
          // TODO MVP do proper locale times
          const formWithFields = {
            ...formstackForm,
            fields: await this.fetchFormstackFieldsData(formstackForm.id)
          }

          const issues = this.validateFormstackFormDef(formWithFields)
          if (issues.length) {
            this.formstackFormDefIssues = issues
            this.formstackIssuesDialog = true
            throw new Error(
              'Invalid form definition:\n' +
                this.formstackFormDefIssues.join('\n')
            )
          } else {
            this.formstackFormDefIssues = []
          }

          template.formstack = (({ name, id, updated }) => ({
            name,
            id,
            updated
          }))(formWithFields)

          // TODO MVP refactor so jsonpack and spacetime are imported simultaneously
          const { default: jsonpack } = await jsonpackImport()

          template.formstack.formDef = jsonpack.pack(formWithFields)

          const { default: spacetime } = await spacetimeImport()

          template.formstack.updated = spacetime(
            template.formstack.updated,
            formWithFields.timezone
          ).d
          template.formstack.url =
            'https://www.formstack.com/admin/form/builder/' +
            template.formstack.id
        }

        if (template.webmerge.id) {
          const webmergeDocument = this.webmergeDocuments.find(
            (item) => +item.id === +template.webmerge.id
          )
          template.webmerge = webmergeDocument
        }

        template.created =
          template.created || firebase.firestore.FieldValue.serverTimestamp()
        template.modified = firebase.firestore.FieldValue.serverTimestamp()
        template.createdBy = template.createdBy || this.$store.state.user.uid
        template.modifiedBy = this.$store.state.user.uid

        const isNew = isNewRecord(template)
        const newTemplate = await saveTemplate(template)

        this.$emit('templateSaved', { templateId: newTemplate.id, isNew })
      } catch (e) {
        this.$showErrorbar('Error saving Template. Refresh and try again.')
        this.$Sentry.captureException(e)
      }

      this.saving = false
    },
    async deleteTemplate() {
      this.saving = true

      var template = this.template
      const name = template.name

      if (
        await this.$refs.confirm.open(
          `Delete ${name}?`,
          'Are you sure you want to delete this template?'
        )
      ) {
        this.$handlingError(`Error deleting template "${name}"`, async () => {
          await deleteTemplate(template)
          this.$emit('templateDeleted', { templateId: template['.key'] })
          this.$showSuccessbar(`Deleted template "${name}"`)
        })
      }
      this.saving = false
    },
    nonNegative(v) {
      const value = parseInt(v)
      return value > -1 || 'This field must be zero or a positive number.'
    },
    isUrl(str) {
      return /^(?:\w+:)?\/\/([^ .]+\.\S{2}|localhost[:?\d]*)\S*$/.test(str)
    },
    cancel() {
      this.$refs.form.reset()
      this.$emit('cancel')
    },
    loadTemplate() {
      this.template = {
        authorUrl: '',
        guideUrl: '',
        creditCost: 1,
        documentType: ASSET_SALE.id,
        ...JSON.parse(JSON.stringify(this.selectedTemplate))
      }
      if (this.isNewTemplate) {
        this.$refs.form && this.$refs.form.reset()
      }
      if (this.selectedTemplate && this.selectedTemplate.created) {
        this.template.created = this.selectedTemplate.created.toDate()
      }
    }
  }
}
</script>
